import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenantScope } from '@/app/providers/TenantScopeProvider';
import { useToast } from '@/app/providers/ToastProvider';
import { ApiError } from '@/shared/api/client';
import { updateCompany } from '@/shared/api/services/company';
import type { Company } from '@/shared/api/types/tenant';

/**
 * Идэвхтэй company-ийн `BusinessType`-ыг солих хуваалцах mutation hook.
 * Backend `PATCH /api/company/{id}` нь RegisterNo/Email/Phone-ыг overwrite
 * хийдэг тул scope companies query cache-аас current company-ийн өгөгдлийг
 * уншиж тэдгээрийг бүхэлд нь дамжуулна.
 *
 * Success-д хариу ирсэн CompanyDto-г шууд cache-руу `setQueryData`-аар
 * бичиж дараа нь invalidate (background refetch) хийнэ — UI шууд шинэчлэгдэх
 * баталгаатай (зөвхөн invalidate нь background refetch завсар хуучин data
 * харуулдаг тул header pill old label-аар үлдэх асуудалтай байсан).
 */
export function useBusinessTypeMutation() {
  const scope = useTenantScope();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (next: number) => {
      const list = qc.getQueryData<Company[]>(['scope', 'companies', scope.tenantId]);
      const current = list?.find((c) => c.Id === scope.companyId);
      if (!current) throw new Error('Идэвхтэй company-ийн мэдээлэл олдсонгүй.');
      return updateCompany(scope.companyId, {
        BusinessType: next,
        RegisterNo: current.RegisterNo ?? null,
        Email: current.Email ?? null,
        Phone: current.Phone ?? null,
      });
    },
    onSuccess: async (updated) => {
      // 1. Cache-ийн company-ийг шууд солих (optimistic) — header switcher +
      //    BusinessTypeGate refetch хүлээлгүй шинэчлэгдэнэ.
      qc.setQueryData<Company[] | undefined>(
        ['scope', 'companies', scope.tenantId],
        (prev) => {
          if (!prev) return prev;
          return prev.map((c) =>
            c.Id === updated.Id ? { ...c, ...updated } : c,
          );
        },
      );
      // 2. Force-ээр refetch хийж backend OnSaving өөрчлөгдсөн талбарууд
      //    (PlanExpiresOn, Status гэх мэт)-ыг sync байлгана. refetchQueries
      //    нь invalidateQueries-аас илүү цэвэр — observer "stale" state-аас
      //    үсрэхгүйгээр шууд network request явуулна.
      await qc.refetchQueries({ queryKey: ['scope', 'companies', scope.tenantId] });
      toast('Бизнесийн төрөл шинэчлэгдлээ.', 'success');
    },
    onError: (e) => {
      toast(
        e instanceof ApiError ? e.message : 'Бизнесийн төрөл шинэчилж чадсангүй.',
        'danger',
      );
    },
  });
}
